/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable no-undef */
/* eslint-disable max-statements */
import { CheckCircleIcon, PencilIcon } from "@heroicons/react/solid";
import { DesktopComputerIcon, LocationMarkerIcon } from "@heroicons/react/outline";
import { FormCheck, Overlay, Popover } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import TextTruncate from "react-text-truncate";
import classNames from "classnames";
import { format } from "date-fns-tz";

import EventPopover from "./EventPopover";

import { eventCancelled, isHybridEvent, registrationIsOpen } from "../../helpers/event";
import { getTimezone } from "../../helpers/dates";
import { useAuth } from "../../services/wp.auth.service";
import { useCourseRegistration } from "../../services/CourseRegistrationProvider";
import { userCanRegister } from "../../helpers/authHelpers";
import utcToZonedTime from "date-fns-tz/utcToZonedTime/index.js";

const timeZone = getTimezone();

const EventCalendarCard = ({ event }) => {

	const { user } = useAuth();
	const { addCourse, registeredFor, removeCourse } = useCourseRegistration();
	const navigate = useNavigate();

	const [showInfo, setShowInfo] = useState(false);
	// eslint-disable-next-line no-unused-vars
	const [target, setTarget] = useState(null);
	const ref = useRef(null);

	const closePopover = useCallback(() => setShowInfo(false), []);
	const handleClick = useCallback((ev) => {
		setShowInfo((show) => !show);
		setTarget(ev.target);
		navigate(`/class/${event.id}`);
	}, [event.id]);
	const handleMouseEnter = useCallback(() => setShowInfo(true), []);
	const handleMouseLeave = useCallback(() => setShowInfo(false), []);

	const startTime = useMemo(() => format(utcToZonedTime(event.start, timeZone), "h:mm"), [event.id]);
	const endTime = useMemo(() => format(utcToZonedTime(event.end, timeZone), "h:mm aaa"), [event.id]);
	const canRegister = useMemo(() => userCanRegister(user) && registrationIsOpen(event), [user, event.id]);
	const hybridEvent = useMemo(() => isHybridEvent(event), [event.id]);

	const registered = useMemo(() => registeredFor(event));
	const inPast = useMemo(() => new Date() > event.end, [event.id]);
	const hasRegistration = useMemo(() => user && !!user?.registrations[event.id], [user]);

	const handleCheckboxChange = useCallback(() => {
		if (registered) {
			removeCourse(event);
		} else {
			addCourse(event);
		}
	}, [registered, event]);

	return (
		<div
			className={classNames(
				"event-calendar-card px-0 w-100 d-flex flex-column rounded-1",
				{
					hasRegistration,
					"in-past": inPast,
					registered
				}
			)}
			onBlur={handleMouseLeave}
			onFocus={handleMouseEnter}
			onKeyPress={handleClick}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			ref={ref}
			role="button"
			tabIndex="0"
		>
			<div
				className="px-2 d-flex flex-column align-items-center justify-content-center h-100"
			>
				<div className="event-header fs-12 w-100 px-0 justify-content-between align-self-start align-items-center pt-1 pb-2">
					<div className="d-flex align-items-center overflow-visible">
						{canRegister && !hasRegistration && !hybridEvent ? (
							<FormCheck
								checked={registered}
								className="d-inline me-1 fs-18 cursor-pointer"
								disabled={!registrationIsOpen(event)}
								name="register"
								onChange={handleCheckboxChange}
							/>) : null}
						{startTime}-{endTime}
					</div>
					<div className="course-type">
						{event.type.indexOf("in-person") !== -1 && <LocationMarkerIcon className="i" />}
						{event.type.indexOf("online") !== -1 && <DesktopComputerIcon className="i" />}
					</div>
				</div>

				<Link
					className={classNames("event-details flex-fill d-flex flex-column justify-content-center", { cancelled: eventCancelled(event) })}
					tabIndex={-1}
					to={`/class/${event.id}`}
				>
					<div className="fs-14 mb-0 fw-bold text-center w-100">
						<TextTruncate line={2} text={event.title} />
						<small className="fw-normal text-italicize">
							<TextTruncate line={1} text={event.locationAbbr || event.location} />
						</small>
					</div>
				</Link>

				{registered ? (
					<div className="status-hint text-green text-center">
						<PencilIcon className="i me-1" />
						Selected for registration
					</div>
				) : null}

				{hasRegistration ? (
					<div className="status-hint text-primary text-center">
						<CheckCircleIcon className="i me-1" />
						You are registered
					</div>
				) : null}
			</div>

			<Overlay
				onHide={closePopover}
				rootClose
				rootCloseEvent="click"
				show={showInfo}
				target={ref}
			>
				<Popover id={event.id}>
					<EventPopover event={event} />
				</Popover>
			</Overlay>

		</div>
	);
};

EventCalendarCard.propTypes = {
	event: PropTypes.shape({
		end: PropTypes.instanceOf(Date),
		id: PropTypes.number,
		location: PropTypes.string,
		locationAbbr: PropTypes.string,
		start: PropTypes.instanceOf(Date),
		title: PropTypes.string.isRequired,
		type: PropTypes.arrayOf(PropTypes.string)
	}).isRequired
};

export default EventCalendarCard;
